/**
 * Mirror of the backend `TerminologyImport` enum — the Amendment #1 A5 value sets.
 *
 * Every earlier value set has its own folder of four near-identical components (OrgType/, Suffix/ …)
 * plus its own redux fields, routes and breadcrumb entries. These three share one set of components
 * driven by this table, so adding a value set is a row here and a row in the backend enum.
 *
 * `key` must match the backend enum constant exactly — it is the `{name}` path variable on
 * /api/externalDataSource/terminology/{name}/import — and `source` must match the
 * DDRule.ExternalSource constant, which is what the logs endpoint filters on.
 *
 * @date: 08/11/26
 * @copy: (C) Copyright 2026 MaxMD Corporation, All Rights Reserved.
 */

export const Terminologies = {
    SpecCat: {
        key: 'SpecCat',
        source: 'SpecCat',
        batchType: 'ImportSpecCatVS',
        label: 'Practice Specialty',
        columns: 'LOC_SPECCAT_CODE, PROV_SPECCAT_CODE',
        // Shown on the import page. This one is expected to fail until DirectTrust publishes the
        // artifact, and an operator who does not know that will read the 404 as a broken deployment.
        notice: 'DirectTrust has not published .../ValueSet/SpecCatVS, so importing from the canonical '
            + 'URL will fail. That is expected. The Practice Specialty list is HL7 c80 practice codes '
            + '(115 curated SNOMED specialties) -- paste '
            + 'https://hl7.org/fhir/R4/valueset-c80-practice-codes.json to import it. The row is keyed '
            + 'on the value set we intend to import rather than on the canonical the fetched artifact '
            + 'declares, so an externally-owned artifact like c80 now takes effect.',
    },
    Facet: {
        key: 'Facet',
        source: 'Facet',
        batchType: 'ImportFacetVS',
        label: 'Facet',
        columns: 'PROV_QUALIFICATION',
        notice: '',
    },
    IalCode: {
        key: 'IalCode',
        source: 'IalCode',
        batchType: 'ImportIalCodeVS',
        label: 'IAL Code',
        columns: 'Endpoint identity assurance level (from the Directory Assistant, not a CSV column)',
        notice: '',
    },
};

export const TerminologyList = Object.values(Terminologies);

/** Case-insensitive, so a hand-typed URL still resolves. Null for anything unrecognized. */
export function lookupTerminology(name) {
    if (!name) {
        return null;
    }
    const match = TerminologyList.find(t => t.key.toLowerCase() === String(name).trim().toLowerCase());
    return match ?? null;
}

export function terminologyByBatchType(type) {
    return TerminologyList.find(t => t.batchType === type) ?? null;
}
